import * as React from 'react';

import { AddressInfo, AddressStatus } from '../types/addressTypes';
import AddressConfirmationPopup from './AddressConfirmationPopup';
import TextInput from './TextInput';
import State from './State';
import { DropdownOption } from './Dropdown';
import NumericInput from './NumericInput';
import { RulesMessages } from '../utils/validator';

export interface AddressProps {
  address1Label: string;
  address2Label: string;
  cityLabel: string;
  stateLabel: string;
  zipLabel: string;
  addressVerificationLabel: string;
  enteredAddressLabel: string;
  rulesMessages: { [key: string]: RulesMessages };
  suggestedAddressLabel: string;
  editAddressBtnLabel: string;
  continueWithAddressBtnLabel: string;
  address: AddressInfo;
  addressStatus: AddressStatus;
  onAddressSave: Function;
  onAddressChange: Function;
  allValid: {
    addressLineOne: boolean;
    city: boolean;
    state: boolean;
    zip: boolean;
  };
  disabled?: boolean;
  autocomplete?: string;
  onEditAddress?: Function;
}

interface AddressState {
  address: AddressInfo;
  focusOnPopupClose?: boolean;
  allValid: {
    addressLineOne: boolean;
    city: boolean;
    state: boolean;
    zip: boolean;
  };
}

export default function Address(props: AddressProps) {
  const [state, setState] = React.useState<AddressState>({
    address: props.address,
    allValid: props.allValid
  });

  React.useEffect(() => {
    if (props.address !== state.address || props.allValid !== state.allValid) {
      setState({ ...state, address: props.address, allValid: props.allValid });
    }
  }, [props.address, props.allValid]);

  React.useEffect(() => {
    props.onAddressChange(state.address, state.allValid);
  }, [state.address]);

  return (
    <>
      <>
        {(props.addressStatus.status === 'MODIFIED' ||
          props.addressStatus.status === 'INVALID') &&
          props.addressStatus.enteredAddress && (
            <AddressConfirmationPopup
              headingText={props.addressVerificationLabel}
              enteredAddressLabel={props.enteredAddressLabel}
              suggestedAddressLabel={props.suggestedAddressLabel}
              editAddressBtnLabel={props.editAddressBtnLabel}
              continueWithAddressBtnLabel={props.continueWithAddressBtnLabel}
              suggestedAddress={props.addressStatus.suggestedAddress}
              enteredAddress={props.addressStatus.enteredAddress}
              onContinueWithAddress={(selectedAddress: AddressInfo) => {
                props.onAddressSave(selectedAddress);
              }}
              onEditAddress={() => {
                if (props.onEditAddress) props.onEditAddress();
                setState({ ...state, focusOnPopupClose: true });
              }}
              onCloseAddressConfirmation={() => {
                setState({ ...state, focusOnPopupClose: true });
              }}
              invalidAddressMsg={
                props.addressStatus.status === 'INVALID' &&
                props.addressStatus.messages &&
                props.addressStatus.messages
              }
            />
          )}
      </>
      <TextInput
        name="addressLine1"
        id="addressLine1"
        label={props.address1Label}
        value={state.address.addressLineOne}
        required
        focus={state.focusOnPopupClose}
        autocomplete={props.autocomplete}
        onChange={(event: any) => {
          setState({
            ...state,
            address: {
              ...state.address,
              addressLineOne: event.target.value
            },
            allValid: {
              ...state.allValid,
              addressLineOne: event.target.valid
            }
          });
        }}
        rulesMessages={props.rulesMessages.addressLine1}
        maxLength={255}
        disabled={props.disabled}
      />
      <TextInput
        name="addressLine2"
        id="addressLine2"
        label={props.address2Label}
        value={state.address.addressLineTwo || ''}
        onChange={(event: any) => {
          setState({
            ...state,
            address: {
              ...state.address,
              addressLineTwo: event.target.value
            }
          });
        }}
        maxLength={255}
        disabled={props.disabled}
      />
      <div className="details">
        <div className="info">
          <TextInput
            name={props.cityLabel}
            id={props.cityLabel}
            label={props.cityLabel}
            value={state.address.city}
            required
            onChange={(event: any) => {
              setState({
                ...state,
                address: {
                  ...state.address,
                  city: event.target.value
                },
                allValid: {
                  ...state.allValid,
                  city: event.target.valid
                }
              });
            }}
            rulesMessages={props.rulesMessages.city}
            maxLength={100}
            disabled={props.disabled}
          />
        </div>
        <div className="info">
          <State
            name={props.stateLabel}
            id={props.stateLabel}
            label={props.stateLabel}
            value={state.address.state}
            required
            onChange={(selectedOption: DropdownOption) => {
              setState({
                ...state,
                address: {
                  ...state.address,
                  state: selectedOption.value
                }
              });
            }}
            disabled={props.disabled}
          />
        </div>
        <div className="info">
          <NumericInput
            name={props.zipLabel}
            id={props.zipLabel.replace(/[^\w]/g, '')}
            label={props.zipLabel}
            value={state.address.zip}
            required
            hasError={false}
            minLength={5}
            maxLength={5}
            onChange={(event: any) => {
              setState({
                ...state,
                address: {
                  ...state.address,
                  zip: event.target.value
                },
                allValid: {
                  ...state.allValid,
                  zip: event.target.valid
                }
              });
            }}
            rulesMessages={props.rulesMessages.zip}
            disabled={props.disabled}
          />
        </div>
      </div>
    </>
  );
}
